import React, { useState, useEffect, useCallback } from 'react';
import SensitiveContent from './SensitiveContent';
import {
    getBiometricInfo,
    getBiometricLabel,
    normalizeBiometricType,
} from '../services/biometricService';

const PASSCODE_LENGTH = 6;

const STEPS = {
    ENTER: 'enter',
    CONFIRM: 'confirm',
    BIOMETRIC: 'biometric',
};

const PasscodeSetup = ({ onComplete, onCancel, isChangeMode = false }) => {
    const [step, setStep] = useState(STEPS.ENTER);
    const [digits, setDigits] = useState('');
    const [firstPasscode, setFirstPasscode] = useState('');
    const [shake, setShake] = useState(false);
    const [error, setError] = useState('');
    const [biometricInfo, setBiometricInfo] = useState({ available: false, type: null });
    const [biometricLoading, setBiometricLoading] = useState(false);
    const [biometricMode, setBiometricMode] = useState('any');

    useEffect(() => {
        let mounted = true;
        (async () => {
            const info = await getBiometricInfo();
            if (!mounted) return;
            setBiometricInfo(info);
            const detected = normalizeBiometricType(info.type);
            if (detected === 'face' || detected === 'touch') setBiometricMode(detected);
        })();
        return () => { mounted = false; };
    }, []);

    const triggerShake = useCallback(() => {
        setShake(true);
        setTimeout(() => setShake(false), 500);
    }, []);

    useEffect(() => {
        if (digits.length !== PASSCODE_LENGTH) return;

        if (step === STEPS.ENTER) {
            setFirstPasscode(digits);
            setDigits('');
            setError('');
            setStep(STEPS.CONFIRM);
        } else if (step === STEPS.CONFIRM) {
            if (digits === firstPasscode) {
                if (biometricInfo.available) {
                    setDigits('');
                    setStep(STEPS.BIOMETRIC);
                } else {
                    onComplete({ passcode: digits, enableBiometric: false });
                }
            } else {
                triggerShake();
                setError('Код-пароли не совпадают');
                setDigits('');
                setTimeout(() => {
                    setFirstPasscode('');
                    setStep(STEPS.ENTER);
                    setError('');
                }, 1200);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [digits]);

    const handleDigit = useCallback((d) => {
        setError('');
        setDigits((prev) => (prev.length < PASSCODE_LENGTH ? prev + d : prev));
    }, []);

    const handleBackspace = useCallback(() => {
        setDigits((prev) => prev.slice(0, -1));
    }, []);

    const handleEnableBiometric = useCallback(async () => {
        setBiometricLoading(true);
        onComplete({ passcode: firstPasscode, enableBiometric: true, biometricMode });
        setBiometricLoading(false);
    }, [firstPasscode, onComplete, biometricMode]);

    const handleSkipBiometric = useCallback(() => {
        onComplete({ passcode: firstPasscode, enableBiometric: false, biometricMode: 'any' });
    }, [firstPasscode, onComplete]);

    const getTitle = () => {
        if (step === STEPS.ENTER) return isChangeMode ? 'Введите новый код-пароль' : 'Придумайте код-пароль';
        if (step === STEPS.CONFIRM) return 'Повторите код-пароль';
        return '';
    };

    if (step === STEPS.BIOMETRIC) {
        const label = getBiometricLabel(biometricInfo.type);
        const detected = normalizeBiometricType(biometricInfo.type);
        const canPickFace = detected === 'unknown' || detected === 'face';
        const canPickTouch = detected === 'unknown' || detected === 'touch';
        const touchOptionLabel = getBiometricLabel('touch');
        return (
            <SensitiveContent>
                <div className="lock-screen">
                    <div className="lock-screen-content">
                        <div className="lock-icon">
                            <svg viewBox="0 0 24 24" className="w-16 h-16" fill="currentColor">
                                <path d="M9 11.75A1.25 1.25 0 1 0 9 9.25a1.25 1.25 0 0 0 0 2.5zm6 0a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm-3 5.5c2.33 0 4.32-1.45 5.12-3.5h-1.67c-.69 1.19-1.97 2-3.45 2s-2.75-.81-3.45-2H6.88c.8 2.05 2.79 3.5 5.12 3.5zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                            </svg>
                        </div>
                        <h2 className="lock-title" style={{ marginBottom: '12px' }}>
                            Использовать {label}?
                        </h2>
                        <p className="text-gray-400 text-sm text-center mb-8 px-4">
                            Вы сможете разблокировать приложение с помощью {label} вместо ввода код-пароля
                        </p>
                        <div className="w-full max-w-[260px] mb-4">
                            <div className="text-gray-400 text-xs mb-2">Разрешённый способ</div>
                            <select
                                className="w-full px-3 py-2 rounded-md bg-[#121212] border border-[#2a2a2a] text-white"
                                value={biometricMode}
                                onChange={(e) => setBiometricMode(e.target.value)}
                            >
                                <option value="any">Любая биометрия</option>
                                <option value="face" disabled={!canPickFace}>Face ID</option>
                                <option value="touch" disabled={!canPickTouch}>{touchOptionLabel}</option>
                            </select>
                        </div>
                        <div className="flex flex-col gap-3 w-full max-w-[260px]">
                            <button
                                className="action-button qr-button"
                                onClick={handleEnableBiometric}
                                disabled={biometricLoading}
                            >
                                {biometricLoading ? 'Подождите...' : `Включить ${label}`}
                            </button>
                            <button
                                className="text-gray-400 text-sm py-2"
                                onClick={handleSkipBiometric}
                            >
                                Пропустить
                            </button>
                        </div>
                    </div>
                </div>
            </SensitiveContent>
        );
    }

    return (
        <SensitiveContent>
            <div className="lock-screen">
                <div className="lock-screen-content">
                    <div className="lock-icon">
                        <svg viewBox="0 0 24 24" className="w-12 h-12" fill="currentColor">
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/>
                        </svg>
                    </div>

                    <h2 className="lock-title">{getTitle()}</h2>

                    <div className={`lock-dots ${shake ? 'lock-shake' : ''}`}>
                        {Array.from({ length: PASSCODE_LENGTH }).map((_, i) => (
                            <div
                                key={i}
                                className={`lock-dot ${i < digits.length ? 'lock-dot-filled' : ''}`}
                            />
                        ))}
                    </div>

                    {error && <p className="lock-error">{error}</p>}

                    <div className="lock-keypad">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                            <button
                                key={d}
                                className="lock-key"
                                onClick={() => handleDigit(String(d))}
                            >
                                {d}
                            </button>
                        ))}
                        <button
                            className="lock-key lock-key-action"
                            onClick={onCancel}
                        >
                            <span className="text-sm">Отмена</span>
                        </button>
                        <button
                            className="lock-key"
                            onClick={() => handleDigit('0')}
                        >
                            0
                        </button>
                        <button
                            className="lock-key lock-key-action"
                            onClick={handleBackspace}
                            disabled={digits.length === 0}
                        >
                            <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                                <path d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </SensitiveContent>
    );
};

export default PasscodeSetup;
